import React, { createContext, useContext, useEffect, useState } from 'react';
import { initializePostHog, getPostHog, isPostHogInitialized } from '@/lib/posthog';
import { supabase } from '@/integrations/supabase/client';

interface PostHogContextType {
  isInitialized: boolean;
  posthog: any;
}

const PostHogContext = createContext<PostHogContextType | undefined>(undefined);

// Provider + consumer hook colocation is the canonical React context pattern;
// React Fast Refresh's per-file rule is a false positive here.
// eslint-disable-next-line react-refresh/only-export-components
export const usePostHog = () => {
  const context = useContext(PostHogContext);
  if (context === undefined) {
    throw new Error('usePostHog must be used within a PostHogProvider');
  }
  return context;
};

interface PostHogProviderProps {
  children: React.ReactNode;
}

export const PostHogProvider: React.FC<PostHogProviderProps> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize PostHog when user authenticates
  useEffect(() => {
    const tryInit = async () => {
      await initializePostHog();
      setIsInitialized(isPostHogInitialized());
    };

    // Try to initialize on mount if already authenticated
    tryInit();

    // Listen for auth state changes to initialize when user logs in
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session && !isPostHogInitialized()) {
        await initializePostHog(session);
        setIsInitialized(isPostHogInitialized());
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const value = {
    isInitialized,
    posthog: getPostHog(),
  };

  return (
    <PostHogContext.Provider value={value}>
      {children}
    </PostHogContext.Provider>
  );
};
